import React from 'react';

import { bemBlock, Icon, Layout, layout, Text } from '@neptune/shared/venus-ui';

import LoadingIndicator from 'components/loading-indicator/LoadingIndicator';

import { InputSize } from '../types';

import './InputWithValidation.less';

export type Validation = 'valid' | 'invalid' | 'pending';

export type InputWithValidationProps = {
  className?: string;
  disabled?: boolean;
  status?: Validation;
  size?: InputSize;
  label?: string;
  role?: string;
  errorMessage?: React.ReactNode;
  span?: layout.Span;
  containerClassName?: string;
} & (
  | {
      type: 'textarea';
      inputProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { 'data-role'?: string };
    }
  | {
      type?: 'input';
      inputProps?: React.InputHTMLAttributes<HTMLInputElement> & { 'data-role'?: string };
    }
);

const block = bemBlock('input-with-validation');

const InputWithValidation = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputWithValidationProps
>((props, ref) => {
  const {
    className,
    disabled,
    status,
    span,
    role,
    size = 'medium',
    type = 'input',
    errorMessage,
    containerClassName,
  } = props;
  const input =
    props.type === 'textarea' ? (
      <textarea
        {...props.inputProps}
        className={block({ element: 'input', extra: props.inputProps?.className })}
        disabled={props.disabled}
        ref={ref as React.RefObject<HTMLTextAreaElement>}
      />
    ) : (
      <input
        type="text"
        {...props.inputProps}
        className={block({ element: 'input', extra: props.inputProps?.className })}
        disabled={props.disabled}
        ref={ref as React.RefObject<HTMLInputElement>}
      />
    );

  return (
    <Layout.Column spacedChildren="xs" component="label" span={span} className={containerClassName}>
      {props.label && (
        <Text color="text" fontWeight="semibold" size="xs">
          {props.label}
        </Text>
      )}
      <Layout.Row
        className={block({
          extra: className,
          modifiers: {
            disabled,
            status,
            size,
            type,
          },
        })}
        alignItems={type === 'textarea' ? 'start' : 'center'}
        withGutter="xs"
        spacedChildren="xs"
        span="auto"
        role={role}
      >
        {input}
        {status && disabled !== true && (
          <>
            {status === 'pending' ? (
              <LoadingIndicator size="small" isVisible />
            ) : (
              <Icon
                className={block({
                  element: 'status',
                  modifiers: { status },
                })}
                glyph={status === 'valid' ? 'check-circle' : 'exclamation-circle'}
              />
            )}
          </>
        )}
      </Layout.Row>
      {errorMessage && status === 'invalid' && (
        <Text color="error" size="xs">
          {errorMessage}
        </Text>
      )}
    </Layout.Column>
  );
});

InputWithValidation.displayName = 'InputWithValidation';

export { InputWithValidation };
